import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams, useLocation } from 'react-router-dom';
import { isAccountAdmin } from 'shared/store/selectors/auth.selector';
import { getWorkspaces } from 'shared/store/selectors/workspace.selector';

const WorkspacesContext = createContext();

/**
 * Get the current workspace and workspaces list from the context and the ability to change the workspace
 * @returns {{
 *  onChangeWorkspace: (workspace: import('../types').Workspace, options?: { setSearchParams: boolean }),
 *  onDeleteWorkspace: () => void,
 *  workspace: import('../types').Workspace,
 *  workspaces: import('../types').Workspace[],
 *  isNew: boolean
 * }}
 */
export function useWorkspacesContext() {
  return useContext(WorkspacesContext);
}

export const WORKSPACE_SEARCH_PARAMS = Object.freeze({
  WORKSPACE_ID: 'wid',
  QUICK_LINK_INDEX: 'ql',
  SCHEDULE_URL_INDEX: 'su',
  COPY_SETTINGS: 'copy',
  MODE: 'mode',
});

const isCreateMode = id => id?.startsWith('new-') || false;

export function WorkspacesProvider({ children }) {
  const workspaces = useSelector(getWorkspaces);
  const [workspace, setWorkspace] = useState(null);
  const userId = useSelector(state => state.auth.user.id);
  const isAdmin = useSelector(isAccountAdmin);

  const managedWorkspaces = useMemo(
    () => (isAdmin ? workspaces : workspaces?.filter(w => w?.managers?.includes(userId))),
    [isAdmin, workspaces, userId],
  );

  const [queryParams, setQueryParams] = useSearchParams();
  const location = useLocation();

  const onChangeWorkspace = useCallback(
    (workspace, options = {}) => {
      const { setSearchParams = false } = options;
      if (setSearchParams) {
        const isNew = isCreateMode(workspace.id);
        setQueryParams(
          new URLSearchParams({
            [WORKSPACE_SEARCH_PARAMS.WORKSPACE_ID]: workspace.id,
          }),
          { replace: true, state: isNew ? { newWorkspace: workspace } : null },
        );
      }
      setWorkspace(workspace);
    },
    [setQueryParams],
  );

  const selected = queryParams.get(WORKSPACE_SEARCH_PARAMS.WORKSPACE_ID);

  // React Router runs navigations inside `startTransition`, so the `setQueryParams`
  // in `onChangeWorkspace` commits *after* the `setWorkspace` beside it. Reading the
  // local workspace as well makes "new" true in the same commit that swaps the
  // editor over — on the search param alone, Add Workspace remounts the name field
  // with `initialEditing={false}` and drops the user in preview instead of edit mode.
  const isNew = isCreateMode(selected) || isCreateMode(workspace?.id);

  useEffect(() => {
    if (!selected && managedWorkspaces?.length > 0) {
      // If no workspace is selected, select the first one
      onChangeWorkspace(managedWorkspaces[0], { setSearchParams: true });
      return;
    }

    // handle refresh
    if (selected && managedWorkspaces?.length > 0 && !workspace) {
      const selectedWorkspace = managedWorkspaces.find(workspace => workspace.id === selected);
      if (selectedWorkspace) {
        setWorkspace(selectedWorkspace);
      } else if (isNew && location.state?.newWorkspace) {
        setWorkspace(location.state.newWorkspace);
      }
      return;
    }

    // The editor first binds to the persisted copy; follow the list when a
    // fresher fetch replaces it, or a reload shows stale settings.
    //
    // This compares by identity, so it also re-binds right after a save: the PUT
    // response is swapped for the list entry `updateWorkspace`'s trailing
    // `fetchWorkspaces()` just wrote. Safe only because `GET /workspace` returns
    // the whole workspace, `operational` included — the dashboard reads the same
    // list. Trim that response and the editor silently loses what it just saved.
    if (workspace && !isNew) {
      const fresh = managedWorkspaces?.find(w => w.id === workspace.id);
      if (fresh && fresh !== workspace) {
        setWorkspace(fresh);
      }
    }
  }, [
    managedWorkspaces,
    selected,
    setQueryParams,
    onChangeWorkspace,
    workspace,
    location.state,
    isNew,
  ]);

  const onDeleteWorkspace = () => {
    onChangeWorkspace(workspaces[0], { setSearchParams: true });
  };

  const value = {
    onChangeWorkspace,
    onDeleteWorkspace,
    workspace,
    workspaces: managedWorkspaces,
    isNew,
  };

  return (
    <WorkspacesContext.Provider value={value}>
      <>{children}</>
    </WorkspacesContext.Provider>
  );
}
